import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ClipboardList, Download, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'in', label: 'Checked In' },
  { key: 'out', label: 'Not Checked In' },
];

export const CheckInReport = ({ attendees, rooms }) => {
  const [filter, setFilter] = useState('all');
  const roomById = Object.fromEntries(rooms.map(r => [r.id, r]));

  const expected = attendees
    .filter(a => a.ticket_status !== 'cancelled' && a.ticket_status !== 'waitlist')
    .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || ''));
  const checkedIn = expected.filter(a => a.checked_in_at);
  const notIn = expected.filter(a => !a.checked_in_at);
  const rows = filter === 'in' ? checkedIn : filter === 'out' ? notIn : expected;

  const roomLabel = (a) => {
    const r = a.room_id ? roomById[a.room_id] : null;
    return r ? `${r.building ? r.building + ' ' : ''}${r.name}` : '';
  };

  const exportCsv = () => {
    const header = ['Last Name', 'First Name', 'Status', 'Checked In At', 'Checked In By', 'Room', 'Email', 'Phone'];
    const lines = rows.map(a => [
      a.last_name, a.first_name,
      a.checked_in_at ? 'Checked In' : 'Not Checked In',
      fmtTime(a.checked_in_at), a.checked_in_by || '',
      roomLabel(a), a.email || '', a.phone || '',
    ]);
    const csv = [header, ...lines].map(r => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const name = filter === 'in' ? 'checked-in.csv' : filter === 'out' ? 'not-checked-in.csv' : 'check-in-report.csv';
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: name });
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} men`);
  };

  return (
    <Card className="border-0 shadow-sm mt-6" data-testid="check-in-report">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-gray-400" />
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Check-In Report</p>
            <span className="text-xs text-gray-400">· {checkedIn.length} in · {notIn.length} not yet</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${filter === f.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
                  data-testid={`check-in-filter-${f.key}`}
                >
                  {f.label} ({f.key === 'in' ? checkedIn.length : f.key === 'out' ? notIn.length : expected.length})
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0} data-testid="export-check-in-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b">
                <th className="py-2 pr-4 font-semibold">Name</th>
                <th className="py-2 pr-4 font-semibold">Room</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Checked In</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-gray-400 italic">Nobody in this list</td></tr>
              ) : rows.map(a => (
                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`report-row-${a.id}`}>
                  <td className="py-2 pr-4 font-medium text-gray-800">{a.last_name}, {a.first_name}</td>
                  <td className="py-2 pr-4 text-gray-600">{roomLabel(a) || <span className="text-gray-300">—</span>}</td>
                  <td className="py-2 pr-4">
                    {a.checked_in_at ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />In</Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs"><Clock className="w-3 h-3 mr-1" />Not yet</Badge>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-gray-500 text-xs">{fmtTime(a.checked_in_at) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
